/**
 * 業務フロー図の描画エンジンの入口 — 図 1 面ぶんの中身（帯・節点・札・線・レーン名）をまとめて組む。
 *
 * **業務フロー図はこのエンジンだけで描く。** 業務ルール側の図と共有の枠は別々の理由で変わるので、
 * 配置・線・節点の規則をここに閉じる。外から使うのは共有の枠（`FlowCanvas`）と線の描画
 * （`FlowGutterEdge`）だけで、どちらも受け取ったものを描くだけの部品である。
 *
 * マスターの図・案件から見た図の左右 2 面・ステージの図の主フローが、同じ 1 本を通る。画面ごとに
 * 組むと、札の文言や入口の添え書きが画面によって抜ける。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー図 1 面ぶんのレーン帯・節点・札・線・レーン名を、訳と差分の印つきでまとめて組む。
 * @graph-connects buildDiagramNodes [calls] 節点と札の組み立て
 * @graph-connects buildDiagramEdges [calls] 線の組み立て
 * @graph-connects buildLaneNodes [calls] レーン帯の組み立て
 * @graph-connects buildLaneLabels [calls] レーン名の組み立て
 */
import type { FlowView } from '@cortex/domain-spec-console';
import type { Edge, Node } from '@xyflow/react';

import type { FlowLaneLabels } from '../flow-workspace/flow-canvas.js';
import type { FlowChangeState } from '../flow-workspace/flow-diff-marks.js';
import {
  flowEntryText,
  flowExitTargetText,
  type FlowTextTranslate,
} from '../story-flow/flow-text.js';
import { flowNodeLabelsOf } from '../story-flow/story-flow-detail.js';
import { buildDiagramEdges, buildDiagramNodes, buildLaneLabels, buildLaneNodes } from './build.js';
import { LANE_LABEL_WIDTH } from './constants.js';
import type { BusinessFlowLayout } from './layout.js';

/** `buildBusinessFlowDiagram` の入力。 */
export interface BusinessFlowDiagramInput {
  /** 業務フロー */
  flow: FlowView;
  /** その業務フローの配置 */
  layout: BusinessFlowLayout;
  /** 帯を引く幅（左右比較では広いほうの図の幅） */
  laneWidth: number;
  /** 節点ごとの差分の状態 */
  stateByNodeId: ReadonlyMap<number, FlowChangeState>;
  /** 印を付ける枝の ID */
  markedTransitionIds: ReadonlySet<string>;
  /** 印の種類 */
  markedKind: 'added' | 'removed' | 'none';
  /** 開いている節点 */
  selectedNodeId: number | null;
  /** 節点を押したときに伝える */
  onSelect: (nodeId: number) => void;
  /** 訳 */
  t: FlowTextTranslate;
}

/** 図 1 面ぶんの中身。 */
export interface BusinessFlowDiagram {
  /** 帯・節点・札 */
  nodes: Node[];
  /** 線 */
  edges: Edge[];
  /** 縮尺の外に描くレーン名 */
  laneLabels: FlowLaneLabels;
}

/**
 * 業務フロー図 1 面ぶんの中身を組む。
 *
 * 出口の札は「出口 → 行き先」、止まるの札は「止まる」と出す。開始の錠剤の入口の文と、菱形の
 * 並列の説明は、図の上に出さず指すと出す（`title`）。
 *
 * @param input 業務フロー・配置・差分の印・選択・訳
 * @returns 帯・節点・札・線・レーン名
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フロー図 1 面ぶんの帯・節点・札・線・レーン名をまとめて組み、開始と菱形に指すと出す文を渡す。
 * @graph-connects flowNodeLabelsOf [calls] 節点の中身に出す語
 * @graph-connects flowEntryText [calls] 開始を指すと出す入口の文
 */
export function buildBusinessFlowDiagram(input: BusinessFlowDiagramInput): BusinessFlowDiagram {
  const { flow, layout, laneWidth, t } = input;
  const labels = flowNodeLabelsOf(t);
  const exitText = t('flow.tag.exit', { target: flowExitTargetText(flow.exit, t) });
  const stopText = t('flow.tag.stop');
  const entryHint = `${t('flow.entryTitle')}: ${flowEntryText(flow.entry, t)}`;
  return {
    nodes: [
      ...buildLaneNodes(layout.lanes, laneWidth),
      ...buildDiagramNodes(layout.nodes, {
        stateByNodeId: input.stateByNodeId,
        selectedNodeId: input.selectedNodeId,
        onSelect: input.onSelect,
        labels,
        hintOf: (node) => {
          if (node.kind === 'start') return entryHint;
          if (node.kind === 'parallelSplit' || node.kind === 'parallelJoin') {
            return t(`flow.detail.${node.kind}`, {
              count: flow.transitions.filter((transition) => transition.from === node.nodeId)
                .length,
            });
          }
          return '';
        },
        tagContent: (kind) => (kind === 'exit' ? exitText : stopText),
      }),
    ],
    edges: buildDiagramEdges(layout, {
      markedTransitionIds: input.markedTransitionIds,
      markedKind: input.markedKind,
      backWord: t('flow.back'),
      labels,
    }),
    laneLabels: {
      rows: buildLaneLabels(layout.lanes, {
        unassigned: t('storyFlow.laneUnassigned'),
        unset: t('spec.unset'),
        untranslated: t('storyFlow.untranslated'),
      }),
      width: LANE_LABEL_WIDTH,
    },
  };
}
